import type { RaceResult, TourState } from '../../shared/types.js';
import type { HudData } from '../game/game.js';
import { num, plural, t } from '../i18n/i18n.js';
import { clear, el, formatTime, hex, icon, ordinal } from './dom.js';
import { PerfGraph } from './perfGraph.js';
import { StreetSign } from './streetSign.js';

/**
 * The in-race overlay: position, timer, checkpoints, speed, minimap, countdown and the
 * results table.
 *
 * Every readout is a pre-built node whose `textContent` is rewritten in place. Rebuilding
 * the HUD each frame would allocate and reflow sixty times a second next to a renderer
 * that is already using the whole frame budget.
 */
export interface MinimapControlState {
  mode: 'follow' | 'overview';
  canZoomIn: boolean;
  canZoomOut: boolean;
}

export interface HudOptions {
  /** Positive steps zoom the minimap in. */
  onMinimapZoom(step: number): void;
  onMinimapMode(): void;
  /** A tap on the minimap: the street map, with the names on. */
  onMinimapOpen(): void;
}

export class Hud {
  readonly root: HTMLElement;
  readonly minimapCanvas: HTMLCanvasElement;
  /** Frame time and ping, under the minimap. Drawn by `main.ts`, which has the socket. */
  readonly perf = new PerfGraph();
  /** The plate for the street the car has just turned onto. Fed by `main.ts` from `Game`. */
  readonly street = new StreetSign();

  private readonly minimapZoomIn: HTMLButtonElement;
  private readonly minimapZoomOut: HTMLButtonElement;
  private readonly minimapModeButton: HTMLButtonElement;

  private readonly positionValue: HTMLElement;
  private readonly positionTotal: HTMLElement;
  private readonly timerValue: HTMLElement;
  private readonly checkpointValue: HTMLElement;
  /** Who else is out driving, in a free drive — the one number it has. */
  private readonly driversValue: HTMLElement;
  private readonly distanceValue: HTMLElement;
  private readonly speedValue: HTMLElement;
  private readonly countdown: HTMLElement;
  private readonly banner: HTMLElement;
  private readonly toasts: HTMLElement;
  private readonly netNote: HTMLElement;
  private readonly resultsPanel: HTMLElement;
  private readonly resultsTitle: HTMLElement;
  private readonly resultsHead: HTMLElement;
  private readonly resultsBody: HTMLElement;
  private readonly resultsActions: HTMLElement;
  private readonly standingsPanel: HTMLElement;
  private readonly standingsBody: HTMLElement;
  private readonly standingsTitle: HTMLElement;
  private readonly spectateTag: HTMLElement;
  /** The street the car is on, under the minimap. The plate says it once; this keeps saying it. */
  private readonly where: HTMLElement;

  private lastCountdown: number | null = null;
  private lastSpectateText = '';
  private goTimer: number | null = null;

  constructor(private readonly options: HudOptions) {
    this.positionValue = el('span', { class: 'metric-value' }, ordinal(1));
    this.positionTotal = el('span', { class: 'metric-sub' }, t('of {count}', { count: 1 }));
    this.timerValue = el('span', { class: 'metric-value tabular' }, '00:00.000');
    this.checkpointValue = el('span', { class: 'metric-value tabular' }, '0/0');
    this.driversValue = el('span', { class: 'metric-value' }, '');
    this.distanceValue = el('span', { class: 'metric-sub tabular' }, '0 m');
    this.speedValue = el('span', { class: 'speed-value tabular' }, '0');
    this.countdown = el('div', { class: 'countdown' });
    this.banner = el('div', { class: 'banner' });
    this.toasts = el('div', { class: 'toasts' });
    this.netNote = el('div', { class: 'hud-net' }, '');
    this.spectateTag = el('div', { class: 'spectate-tag' });
    this.where = el('div', { class: 'hud-where', id: 'hud-where' });
    this.minimapCanvas = el('canvas', { class: 'minimap', width: 200, height: 200 });
    this.minimapCanvas.addEventListener('click', () => this.options.onMinimapOpen());
    this.minimapZoomIn = mapButton('+', t('Zoom in (])'), () => this.options.onMinimapZoom(1));
    this.minimapZoomOut = mapButton('−', t('Zoom out ([)'), () => this.options.onMinimapZoom(-1));
    this.minimapModeButton = mapButton('N', t('North-up / heading-up (N)'), () =>
      this.options.onMinimapMode(),
    );

    this.resultsBody = el('tbody', {});
    this.resultsHead = el('thead', {});
    this.resultsActions = el('div', { class: 'results-actions' });
    this.resultsTitle = el('h2', { class: 'results-title' }, t('Race complete'));
    this.standingsTitle = el('h3', { class: 'panel-title' }, t('Standings'));
    this.standingsBody = el('tbody', {});
    this.standingsPanel = el(
      'div',
      { class: 'results-standings is-hidden' },
      this.standingsTitle,
      el('table', { class: 'results-standings-table' }, this.standingsBody),
    );
    this.resultsPanel = el(
      'div',
      { class: 'results is-hidden' },
      el(
        'div',
        { class: 'results-card' },
        this.resultsTitle,
        el('table', { class: 'results-table' }, this.resultsHead, this.resultsBody),
        this.standingsPanel,
        this.resultsActions,
      ),
    );

    this.root = el(
      'div',
      { class: 'hud' },
      this.spectateTag,
      this.street.root,
      el(
        'div',
        { class: 'hud-top-left' },
        metric(t('Position'), this.positionValue, this.positionTotal),
        metric(t('Time'), this.timerValue),
        metric(t('Checkpoint'), this.checkpointValue, this.distanceValue),
        el('div', { class: 'hud-free-drive' }, metric(t('Free drive'), this.driversValue)),
      ),
      el(
        'div',
        { class: 'hud-top-right' },
        el(
          'div',
          { class: 'minimap-frame' },
          this.minimapCanvas,
          // Says the map opens. Not a button of its own: on a phone the minimap is 112 px
          // and its three buttons fill the bottom of it, so the whole map is the button.
          el('span', { class: 'minimap-expand' }, icon('open_in_full')),
          el(
            'div',
            { class: 'minimap-controls' },
            this.minimapZoomIn,
            this.minimapZoomOut,
            this.minimapModeButton,
          ),
        ),
        this.where,
        this.perf.root,
        this.netNote,
      ),
      el(
        'div',
        { class: 'hud-bottom' },
        el(
          'div',
          { class: 'speedo' },
          this.speedValue,
          el('span', { class: 'speed-unit' }, 'km/h'),
        ),
        // The line of keyboard hints that used to sit here is in the Escape menu now. It
        // was on screen for the whole of every race to answer a question you have once.
      ),
      this.countdown,
      this.banner,
      this.toasts,
      this.resultsPanel,
    );
  }

  update(data: HudData): void {
    // Once the flyover takes over, the speedometer and the checkpoint arrow are about a
    // car nobody is driving. Hide them and say what the camera is doing instead.
    this.root.classList.toggle('is-spectating', data.spectating);
    // On the grid there is no race yet at all: a lap timer reading zero and a position
    // reading 1st are both lies, and the staging overlay is saying the true thing already.
    this.root.classList.toggle('is-staging', data.staging);
    // A free drive has no place, no clock and no gate, so the three race readouts give way
    // to the one thing worth knowing: who else is out there.
    this.root.classList.toggle('is-free', data.freeDrive);
    if (data.freeDrive) {
      this.driversValue.textContent = plural(data.playerCount, '{n} driver', '{n} drivers');
    }
    const spectateText = !data.spectating
      ? ''
      : data.spectatingName
        ? t('Watching {name}', { name: data.spectatingName })
        : t('Finish line');
    if (spectateText !== this.lastSpectateText) {
      this.lastSpectateText = spectateText;
      this.spectateTag.textContent = spectateText;
    }

    this.positionValue.textContent = ordinal(data.position);
    this.positionTotal.textContent = t('of {count}', { count: data.playerCount });
    this.timerValue.textContent = formatTime(data.raceTimeMs);
    this.checkpointValue.textContent = `${data.checkpointsPassed}/${data.checkpointsTotal}`;
    this.distanceValue.textContent =
      data.distanceToNext < 1000
        ? t('{m} m away', { m: Math.round(data.distanceToNext) })
        : t('{km} km away', { km: num(data.distanceToNext / 1000, 2) });
    this.speedValue.textContent = String(Math.round(data.speedKmh));
    const where = data.street ?? '';
    if (this.where.textContent !== where) this.where.textContent = where;

    if (data.countdown !== this.lastCountdown) {
      this.lastCountdown = data.countdown;
      if (data.countdown !== null && this.goTimer !== null) {
        // A fresh countdown outranks the GO still fading from the last one — a restart is
        // the one way to see both inside a second.
        window.clearTimeout(this.goTimer);
        this.goTimer = null;
        this.countdown.classList.remove('is-go');
      }
      if (data.countdown === null) {
        this.countdown.textContent = '';
        this.countdown.classList.remove('is-visible');
      } else {
        this.countdown.textContent = data.countdown > 0 ? String(data.countdown) : t('GO');
        this.countdown.classList.add('is-visible');
        // Retrigger the pop animation on each new number.
        this.countdown.classList.remove('pop');
        void this.countdown.offsetWidth;
        this.countdown.classList.add('pop');
      }
    }
  }

  /**
   * The green light, in the same enormous type as the numbers that led up to it.
   *
   * The countdown owns the middle of the screen at 12rem and then hands over to… a small
   * grey banner in the corner. The one moment the whole grid is waiting for was the
   * quietest thing on screen. This puts GO where the 3, 2 and 1 were, and holds it there
   * long enough to register.
   *
   * It cannot simply be the `countdown === 0` frame: the server clears the countdown at the
   * same instant it starts the race, so that frame may never be rendered at all.
   */
  showGo(durationMs = 1000): void {
    // Parks `lastCountdown` on the value `update` will keep seeing, so the per-frame path
    // finds nothing changed and leaves this alone until the timer takes it away.
    this.lastCountdown = null;
    this.countdown.textContent = t('GO!');
    this.countdown.classList.add('is-visible', 'is-go');
    this.countdown.classList.remove('pop');
    void this.countdown.offsetWidth;
    this.countdown.classList.add('pop');
    if (this.goTimer !== null) window.clearTimeout(this.goTimer);
    this.goTimer = window.setTimeout(() => {
      this.goTimer = null;
      this.countdown.classList.remove('is-visible', 'is-go');
      this.countdown.textContent = '';
    }, durationMs);
  }

  /**
   * Trouble with the connection, under the perf graph.
   *
   * The status pill this replaces was on screen permanently to say "connected", which is
   * the state you can already infer from the game working. Empty text hides the node, so
   * the only time it costs a pixel is the only time it says anything.
   */
  setNetTrouble(text: string): void {
    this.netNote.textContent = text;
  }

  /** Reflects what the map will actually do, so a dead button is visibly dead. */
  setMinimapState(state: MinimapControlState): void {
    this.minimapZoomIn.disabled = !state.canZoomIn;
    this.minimapZoomOut.disabled = !state.canZoomOut;
    this.minimapModeButton.classList.toggle('is-active', state.mode === 'overview');
    this.minimapModeButton.title =
      state.mode === 'overview'
        ? t('Whole region, north up — press N to follow your car')
        : t('Following your car — press N for the whole region');
  }

  showBanner(text: string, durationMs = 2600): void {
    this.banner.textContent = text;
    this.banner.classList.add('is-visible');
    window.setTimeout(() => this.banner.classList.remove('is-visible'), durationMs);
  }

  toast(message: string, kind: 'info' | 'warn' | 'error' = 'info', durationMs = 6000): void {
    const node = el('div', { class: `toast is-${kind}` }, message);
    this.toasts.append(node);
    window.setTimeout(() => {
      node.classList.add('is-leaving');
      window.setTimeout(() => node.remove(), 320);
    }, durationMs);
  }

  showResults(
    results: RaceResult[],
    localId: string,
    actions: HTMLElement[],
    tour: TourState | null = null,
  ): void {
    // The points column appears only when there are points, so a one-off race gets exactly
    // the table it always had.
    const scored = results.some((r) => r.points !== undefined);

    this.resultsTitle.textContent = tour
      ? t('Race {n} of {total} — {title}', {
          n: tour.index + 1,
          total: tour.stops.length,
          title: tour.stops[tour.index]?.title ?? '',
        })
      : t('Race complete');

    clear(this.resultsHead);
    this.resultsHead.append(
      el(
        'tr',
        {},
        el('th', {}, '#'),
        el('th', {}, t('Driver')),
        el('th', {}, t('Time')),
        el('th', {}, t('Gates')),
        scored ? el('th', {}, t('Pts')) : null,
      ),
    );

    clear(this.resultsBody);
    for (const [index, result] of results.entries()) {
      const isSelf = result.id === localId;
      const row = el(
        'tr',
        { class: isSelf ? 'is-self' : '' },
        el('td', { class: 'tabular' }, result.finished ? ordinal(result.position ?? index + 1) : '—'),
        el(
          'td',
          {},
          el('span', { class: 'swatch', style: { background: hex(result.color) } }),
          el('span', {}, result.name),
          result.ai && el('span', { class: 'tag' }, 'AI'),
          !result.connected && !result.ai && el('span', { class: 'tag' }, t('left')),
        ),
        el('td', { class: 'tabular' }, result.timeMs !== null ? formatTime(result.timeMs) : 'DNF'),
        el('td', { class: 'tabular' }, String(result.checkpointsPassed)),
        scored
          ? el('td', { class: 'tabular results-points' }, `+${result.points ?? 0}`)
          : null,
      );
      this.resultsBody.append(row);
    }

    this.showStandings(tour, localId);

    clear(this.resultsActions);
    for (const action of actions) this.resultsActions.append(action);
    this.resultsPanel.classList.remove('is-hidden');
  }

  /**
   * The cup so far, under the race that just ran.
   *
   * The movement arrow is the reason this exists rather than a second points column: after
   * race three what people want to know is not who has 31 points, it is who just went past
   * them.
   */
  private showStandings(tour: TourState | null, localId: string): void {
    const show = Boolean(tour && !tour.complete && tour.stops.length > 1);
    this.standingsPanel.classList.toggle('is-hidden', !show);
    if (!tour || !show) return;

    const left = tour.stops.length - (tour.index + 1);
    this.standingsTitle.textContent = plural(left, 'Standings · {n} race to go', 'Standings · {n} races to go');

    clear(this.standingsBody);
    for (const row of tour.standings) {
      const delta =
        row.delta > 0 ? `▲${row.delta}` : row.delta < 0 ? `▼${-row.delta}` : '';
      this.standingsBody.append(
        el(
          'tr',
          { class: row.id === localId ? 'is-self' : '' },
          el('td', { class: 'tabular' }, String(row.rank)),
          el(
            'td',
            {},
            el('span', { class: 'swatch', style: { background: hex(row.color) } }),
            el('span', {}, row.name),
            row.ai ? el('span', { class: 'tag' }, 'AI') : null,
          ),
          el(
            'td',
            {
              class: `tabular results-delta${row.delta > 0 ? ' is-up' : row.delta < 0 ? ' is-down' : ''}`,
            },
            delta,
          ),
          el('td', { class: 'tabular podium-points' }, String(row.points)),
        ),
      );
    }
  }

  hideResults(): void {
    this.resultsPanel.classList.add('is-hidden');
  }

  reset(): void {
    this.hideResults();
    this.lastCountdown = null;
    if (this.goTimer !== null) window.clearTimeout(this.goTimer);
    this.goTimer = null;
    this.countdown.textContent = '';
    this.countdown.classList.remove('is-visible', 'is-go');
    this.banner.classList.remove('is-visible');
  }
}

function mapButton(text: string, title: string, onClick: () => void): HTMLButtonElement {
  const button = el('button', { class: 'minimap-button', type: 'button', title }, text);
  button.addEventListener('click', onClick);
  return button;
}

function metric(label: string, value: HTMLElement, sub?: HTMLElement): HTMLElement {
  return el(
    'div',
    { class: 'metric' },
    el('span', { class: 'metric-label' }, label),
    value,
    sub ?? null,
  );
}
